import { useCallback, useEffect, useState } from 'react';

import type { ADFEntity } from '@atlaskit/adf-utils/types';

import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';

import type { CreateSnippetBody } from '../services/blockServiceClient.ts';
import { createSnippet } from '../services/blockServiceClient.ts';
import type { Snippet } from '../snippetTypes.ts';
import { SnippetBodyFormat, SnippetProduct } from '../snippetTypes.ts';

export const useCreateSnippet = (
	onSuccess: (snippet: Snippet) => void,
	onFailure: () => void,
) => {
	const [error, setError] = useState(null);
	const [loading, setLoading] = useState(false);

	const create = useCallback(
		async (name: string, body: ADFEntity, description?: string): Promise<Snippet | null> => {
			const params: CreateSnippetBody = {
				name,
				description,
				body: JSON.stringify(body),
				bodyFormat: SnippetBodyFormat.ADF,
				product: SnippetProduct.CONFLUENCE,
			};

			try {
				setLoading(true);

				const newSnippet = await createSnippet(params);
				onSuccess(newSnippet);
				return newSnippet;
			} catch (thrownError) {
				setError(thrownError);
				onFailure();
			} finally {
				setLoading(false);
			}

			return null;
		},
		[onSuccess, setError, setLoading, onFailure],
	);

	useEffect(() => {
		if (!loading && error) {
			getMonitoringClient().submitError(error, {
				attribution: Attribution.INTEGRATIONS,
			});
		}
	}, [error, loading]);

	return { create, error, loading };
};
